import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, ExternalLink } from 'lucide-react';
import { Logo } from './Logo';
import { BRAND, CONTACT, PARTNERS } from '../lyphline';

const Footer = () => (
  <footer className="bg-[#003456] text-slate-300 border-t border-white/10">
    {/* Partner / product lines strip */}
    <div className="border-b border-white/10">
      <div className="max-w-7xl mx-auto px-6 py-10">
        <p className="text-xs uppercase tracking-[0.25em] text-white/40 text-center mb-6">Part of the freeze dry family</p>
        <div className="flex flex-col sm:flex-row items-stretch justify-center gap-4">
          {[PARTNERS.home, PARTNERS.commercial].map((p) => (
            <a
              key={p.name}
              href={p.url}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex-1 max-w-sm mx-auto sm:mx-0 flex items-center justify-between gap-4 bg-[#023F63] border border-white/10 hover:border-[#CE8946]/50 rounded-xl px-6 py-4 transition-colors"
              data-testid={`footer-partner-${p.name.toLowerCase()}`}
            >
              <div>
                <div className="text-white font-bold text-lg group-hover:text-[#CE8946] transition-colors">{p.name}</div>
                <div className="text-sm text-white/50">{p.blurb}</div>
              </div>
              <ExternalLink className="h-4 w-4 text-white/40 group-hover:text-[#CE8946] transition-colors flex-shrink-0" />
            </a>
          ))}
        </div>
      </div>
    </div>

    <div className="max-w-7xl mx-auto px-6 py-14">
      <div className="grid md:grid-cols-4 gap-10">
        {/* Brand */}
        <div className="md:col-span-2">
          <Logo size="lg" />
          <p className="text-slate-400 text-sm mt-5 max-w-md leading-relaxed">
            Expert diagnostics, rebuilds and modifications for freeze dryers of varying sizes.
            Get your machine back to peak performance, fast.
          </p>
          <Link
            to="/repair/quote"
            className="inline-flex items-center gap-2 mt-6 bg-[#CE8946] hover:bg-[#B87333] text-[#003456] font-bold text-sm px-5 py-2.5 rounded-md transition-colors"
            data-testid="footer-quote-cta"
          >
            Get Your Free Diagnostic Quote
          </Link>
        </div>

        {/* Services */}
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-widest text-white/40 mb-4">Services</h4>
          <ul className="space-y-2.5 text-slate-400 text-sm">
            <li><Link to="/#services" className="hover:text-[#CE8946]">Diagnostics & Assessment</Link></li>
            <li><Link to="/#rebuilds" className="hover:text-[#CE8946]">Rebuilds & Maintenance</Link></li>
            <li><Link to="/#retrofits" className="hover:text-[#CE8946]">Modifications</Link></li>
            <li><Link to="/process-rates" className="hover:text-[#CE8946]">Process & Rates</Link></li>
            <li><Link to="/financing" className="hover:text-[#CE8946]">Financing Support</Link></li>
          </ul>
        </div>

        {/* Contact */}
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-widest text-white/40 mb-4">Contact</h4>
          <ul className="space-y-3 text-slate-400 text-sm">
            <li>
              <a href={CONTACT.phoneHref} className="flex items-center gap-2 hover:text-[#CE8946]">
                <Phone className="h-4 w-4 text-[#CE8946]" /> {CONTACT.phone}
              </a>
            </li>
            <li>
              <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-2 hover:text-[#CE8946]">
                <Mail className="h-4 w-4 text-[#CE8946]" /> {CONTACT.email}
              </a>
            </li>
            <li className="flex items-start gap-2">
              <MapPin className="h-4 w-4 text-[#CE8946] mt-0.5 flex-shrink-0" />
              <span>{CONTACT.address}</span>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10 mt-10 pt-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-slate-500 text-sm">
        <p>&copy; {new Date().getFullYear()} {BRAND.full}. {BRAND.parent}.</p>
        <div className="flex gap-5">
          <Link to="/privacy-policy" className="hover:text-[#CE8946]">Privacy</Link>
          <Link to="/terms-of-use" className="hover:text-[#CE8946]">Terms</Link>
          <Link to="/admin" className="hover:text-[#CE8946]" data-testid="footer-admin-link">Staff Login</Link>
        </div>
      </div>
    </div>
  </footer>
);

export default Footer;
